import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { Store } from 'vuex';

// vue-i18n 9 has no default export; the retry interceptor only needs `t`.
type Translator = { t: (key: string, ...args: any[]) => string };

const MAX_RETRIES = 3;
const OFFLINE_WAIT_MS = 10_000;

interface RetryConfig extends AxiosRequestConfig {
    __retryCount?: number;
    __noRetry?: boolean;
}

function isTransientNetworkError(error: AxiosError): boolean {
    if (axios.isCancel(error)) return false;
    const code = (error as any).code;
    if (code === 'ERR_NETWORK' || code === 'ECONNABORTED') return true;
    if (error.response) return false;
    return !code;
}

function backoffDelay(attempt: number): number {
    return Math.min(8000, 500 * Math.pow(2, attempt)) + Math.random() * 250;
}

function waitForOnline(maxWait: number): Promise<void> {
    if (navigator.onLine) return Promise.resolve();
    return new Promise(resolve => {
        let settled = false;
        const cleanup = () => {
            if (settled) return;
            settled = true;
            window.removeEventListener('online', onOnline);
            resolve();
        };
        const onOnline = () => cleanup();
        window.addEventListener('online', onOnline);
        setTimeout(cleanup, maxWait);
    });
}

export function installRetryInterceptor(store: Store<any>, i18n: Translator): void {
    axios.interceptors.response.use(
        (response: AxiosResponse) => {
            const config = response.config as RetryConfig;
            if ((config?.__retryCount ?? 0) > 0) {
                store.commit('SET_NOTIFICATIONTEXT', i18n.t('message.connectionRestored'));
            }
            return response;
        },
        async (error: AxiosError) => {
            if (!error.config) return Promise.reject(error);
            const config = error.config as RetryConfig;
            const method = (config.method ?? 'get').toLowerCase();
            const retryCount = config.__retryCount ?? 0;

            const eligible =
                method === 'get' &&
                isTransientNetworkError(error) &&
                config.__noRetry !== true &&
                retryCount < MAX_RETRIES;

            if (!eligible) {
                return Promise.reject(error);
            }

            store.commit('SET_NOTIFICATIONTEXT', i18n.t('message.connectionLostRetrying'));

            await waitForOnline(OFFLINE_WAIT_MS);

            if (store.state.isLoggedIn === false) {
                return Promise.reject(error);
            }

            await new Promise(resolve => setTimeout(resolve, backoffDelay(retryCount)));

            config.__retryCount = retryCount + 1;
            return axios(config);
        }
    );
}
